import React, { useState, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { usePermissions } from '../App';
import { formatCurrency } from '../utils/currency';

/**
 * BatchBilling Component
 * 
 * Month-end batch billing center for high-volume legal firms.
 * Aggregates unbilled work by client, allows multi-selection,
 * and generates draft invoices in batch.
 * 
 * Features:
 * - Client selection with checkboxes
 * - Floating footer showing selected count and total WIP
 * - One-click batch invoice generation
 * - Visual feedback with loading states
 */
const BatchBilling = ({ authAxios, showToast, fetchInvoices }) => {
  const [unbilledByClient, setUnbilledByClient] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUnbilledActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUnbilledActivity = async () => {
    try {
      setLoading(true);
      const resp = await authAxios.get('/invoices/unbilled_activity/');
      setUnbilledByClient(resp.data.results || resp.data || {});
    } catch (err) {
      showToast('Failed to load unbilled activity', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClient = (clientId) => {
    const newSet = new Set(selectedClients);
    if (newSet.has(clientId)) {
      newSet.delete(clientId);
    } else {
      newSet.add(clientId);
    }
    setSelectedClients(newSet);
  };

  const handleSelectAll = () => {
    const allIds = Object.entries(unbilledByClient)
      .map(([_, buckets]) => {
        const first = (buckets.time_entries?.[0] || buckets.expense_entries?.[0]) || {};
        return first.client_id || null;
      })
      .filter(id => id !== null);
    
    if (selectedClients.size === allIds.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(allIds));
    }
  };

  const handleGenerateBatch = async () => {
    if (selectedClients.size === 0) {
      showToast('Please select at least one client', 'warning');
      return;
    }

    try {
      setGenerating(true);
      const clientIds = Array.from(selectedClients);
      
      // Generate invoices sequentially to avoid race conditions
      let successCount = 0;
      let failCount = 0;
      
      for (const clientId of clientIds) {
        try {
          await authAxios.post('/invoices/generate_from_wip/', { client_id: clientId });
          successCount++;
        } catch (err) {
          failCount++;
          console.error(`Failed to generate invoice for client ${clientId}:`, err);
        }
      }

      if (successCount > 0) {
        showToast(`Successfully generated ${successCount} draft invoice(s)${failCount > 0 ? ` (${failCount} failed)` : ''}`, 'success');
        await fetchInvoices();
        await fetchUnbilledActivity();
        setSelectedClients(new Set());
      } else {
        showToast('Failed to generate any invoices', 'error');
      }
    } catch (err) {
      showToast('Batch generation failed', 'error');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const calculateTotals = () => {
    let totalWIP = 0;
    let selectedWIP = 0;

    Object.entries(unbilledByClient).forEach(([_, buckets]) => {
      const timeEntries = buckets.time_entries || [];
      const expenseEntries = buckets.expense_entries || [];
      
      const clientTotal = 
        timeEntries.reduce((sum, t) => sum + (parseFloat(t.total_fee) || 0), 0) +
        expenseEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
      
      totalWIP += clientTotal;
      
      const first = (timeEntries[0] || expenseEntries[0]) || {};
      if (first.client_id && selectedClients.has(first.client_id)) {
        selectedWIP += clientTotal;
      }
    });

    return { totalWIP, selectedWIP };
  };

  const filteredClients = Object.entries(unbilledByClient).filter(([clientName]) =>
    clientName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { totalWIP, selectedWIP } = calculateTotals();
  const permissions = usePermissions();

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen pb-32">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Batch Billing Center</h2>
        <p className="text-gray-600">Select clients and generate draft invoices for month-end billing run</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-600">Total Clients</h3>
          <p className="text-3xl font-bold text-indigo-600 mt-2">{Object.keys(unbilledByClient).length}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-600">Total Unbilled WIP</h3>
          <p className="text-3xl font-bold text-green-600 mt-2">{formatCurrency(totalWIP)}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-600">Selected Clients</h3>
          <p className="text-3xl font-bold text-purple-600 mt-2">{selectedClients.size}</p>
        </div>
      </div>

      {/* Search and Select All */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex items-center justify-between gap-4">
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-md border border-indigo-600 font-medium"
          >
            {selectedClients.size === Object.keys(unbilledByClient).length ? 'Deselect All' : 'Select All'}
          </button>
          <button
            onClick={fetchUnbilledActivity}
            className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-md border border-gray-300 font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Client List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredClients.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? 'No clients match your search' : 'No unbilled activity found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedClients.size === filteredClients.length && filteredClients.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time Entries
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expenses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total WIP
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClients.map(([clientName, buckets]) => {
                  const timeEntries = buckets.time_entries || [];
                  const expenseEntries = buckets.expense_entries || [];
                  const first = (timeEntries[0] || expenseEntries[0]) || {};
                  const clientId = first.client_id || null;
                  
                  const timeTotal = timeEntries.reduce((sum, t) => sum + (parseFloat(t.total_fee) || 0), 0);
                  const expTotal = expenseEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
                  const total = timeTotal + expTotal;

                  return (
                    <tr 
                      key={clientName}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedClients.has(clientId) ? 'bg-indigo-50' : ''}`}
                      onClick={() => clientId && handleToggleClient(clientId)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={clientId ? selectedClients.has(clientId) : false}
                          onChange={() => clientId && handleToggleClient(clientId)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{clientName}</div>
                        <div className="text-sm text-gray-500">ID: {clientId || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{timeEntries.length} entries</div>
                        <div className="text-sm text-gray-500">{formatCurrency(timeTotal)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{expenseEntries.length} entries</div>
                        <div className="text-sm text-gray-500">{formatCurrency(expTotal)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{formatCurrency(total)}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Floating Action Footer */}
      {selectedClients.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-sm text-gray-600">Selected Clients</div>
                <div className="text-2xl font-bold text-indigo-600">{selectedClients.size}</div>
              </div>
              <div className="h-12 border-l border-gray-300"></div>
              <div>
                <div className="text-sm text-gray-600">Selected WIP</div>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(selectedWIP)}</div>
              </div>
            </div>
              <div className="flex items-center gap-4">
              <button
                onClick={() => setSelectedClients(new Set())}
                className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                disabled={generating}
              >
                Clear Selection
              </button>
              <Tippy content={permissions.canCreateInvoice ? '' : 'Only partners may generate invoices from WIP.'} disabled={permissions.canCreateInvoice}>
                <div>
                  <button
                    onClick={handleGenerateBatch}
                    disabled={generating || !permissions.canCreateInvoice}
                    className={`px-8 py-3 ${permissions.canCreateInvoice ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed'} rounded-md font-medium flex items-center gap-2`}
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Generating...
                      </>
                    ) : (
                      `Generate ${selectedClients.size} Invoice${selectedClients.size !== 1 ? 's' : ''}`
                    )}
                  </button>
                </div>
              </Tippy>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchBilling;
